import { useEffect } from 'react';
import type { SeoDefinition } from '../calculators/types';
import { absoluteUrl, SITE_NAME } from './site';
import type { StructuredData } from './structuredData';

function ensureMeta(name: string): HTMLMetaElement {
  let element = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);

  if (!element) {
    element = document.createElement('meta');
    element.name = name;
    document.head.append(element);
  }

  return element;
}

function ensurePropertyMeta(property: string): HTMLMetaElement {
  let element = document.querySelector<HTMLMetaElement>(`meta[property="${property}"]`);

  if (!element) {
    element = document.createElement('meta');
    element.setAttribute('property', property);
    document.head.append(element);
  }

  return element;
}

function ensureCanonicalLink(): HTMLLinkElement {
  let element = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');

  if (!element) {
    element = document.createElement('link');
    element.rel = 'canonical';
    document.head.append(element);
  }

  return element;
}

function ensureStructuredDataScript(): HTMLScriptElement {
  let element = document.querySelector<HTMLScriptElement>('#seo-structured-data');

  if (!element) {
    element = document.createElement('script');
    element.id = 'seo-structured-data';
    element.type = 'application/ld+json';
    document.head.append(element);
  }

  return element;
}

export function resolveCanonicalUrl(canonicalPath: string, origin?: string): string {
  return origin
    ? new URL(canonicalPath, origin).toString()
    : absoluteUrl(canonicalPath);
}

export function useSeo(
  metadata: SeoDefinition,
  structuredData?: StructuredData,
): void {
  const structuredDataJson = structuredData ? JSON.stringify(structuredData) : '';

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    const canonicalUrl = resolveCanonicalUrl(metadata.canonicalPath);

    document.title = metadata.title;
    ensureMeta('description').content = metadata.description;
    ensureMeta('robots').content = metadata.robots ?? 'index,follow';
    ensureCanonicalLink().href = canonicalUrl;

    ensurePropertyMeta('og:type').content = 'website';
    ensurePropertyMeta('og:site_name').content = SITE_NAME;
    ensurePropertyMeta('og:title').content = metadata.title;
    ensurePropertyMeta('og:description').content = metadata.description;
    ensurePropertyMeta('og:url').content = canonicalUrl;

    const structuredDataScript = ensureStructuredDataScript();
    if (structuredDataJson) {
      structuredDataScript.textContent = structuredDataJson;
    } else {
      structuredDataScript.remove();
    }
  }, [metadata.canonicalPath, metadata.description, metadata.robots, metadata.title, structuredDataJson]);
}
